import i18n from '@/i18n';
import { taskAPI } from '@/utils/api';
import { buildRetryTaskApiPatch } from './ipeTaskActions';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { resolveModelStudioPresetForRetry } from './resolveModelStudioPresetForReuse';

async function resolveUrlList(rawUrls) {
  const urls = [];
  for (const raw of rawUrls) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const resolved = await resolveImg2ImgReferenceForApi(trimmed);
    if (resolved) urls.push(resolved);
  }
  return urls;
}

function readOperatorName(task, result) {
  return String(result.operatorName || result.operator || task?.operator || '').trim();
}

function readGenerateCount(result, task) {
  return Math.max(1, Math.min(4, Number(result.generateCount ?? task?.total ?? 1) || 1));
}

async function resolveRetryModelImageUrl({
  navId,
  result = {},
  batchImageCount = 0,
  uploadedErrorMessage = i18n.t('workbench:ipeUtilError.taskMissingModel'),
  presetErrorMessage = i18n.t('workbench:ipeUtilError.presetModelUnresolved'),
} = {}) {
  const { modelImageUrl, meta } = await resolveModelStudioPresetForRetry(navId, result, { batchImageCount });

  if (meta.usedUploadedModel) {
    if (!modelImageUrl) throw new Error(uploadedErrorMessage);
    return { modelImageUrl, meta };
  }

  if (meta.selectedPresetId || meta.selectedPresetUrl || result.modelImageUrl) {
    if (!modelImageUrl) throw new Error(presetErrorMessage);
    return { modelImageUrl, meta };
  }

  return {
    modelImageUrl: null,
    meta: {
      ...meta,
      hasCustomModel: false,
    },
  };
}

function buildRetryReferencePreviews(result, batchImageUrls, modelImageUrl) {
  if (Array.isArray(result.referencePreviews) && result.referencePreviews.length > 0) {
    return result.referencePreviews;
  }
  return [...batchImageUrls, modelImageUrl].filter(Boolean);
}

async function enqueueModelStudioRetry(taskId, estimatedTotal, initialResult) {
  await taskAPI.update(taskId, {
    ...buildRetryTaskApiPatch({
      total: estimatedTotal,
      successCount: 0,
      failedCount: 0,
    }),
    result: {
      ...initialResult,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
      failureHint: '',
    },
  });
  await taskAPI.submit(taskId, { force: true });
  return { taskId };
}

/** 姿势裂变历史重试：重新入队 */
export async function retryModelPoseFissionTask(task) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const prompt = String(r.prompt || r.userPrompt || '').trim();
  const generateCount = readGenerateCount(r, task);
  const rawRefs = Array.isArray(r.modelImageUrls) && r.modelImageUrls.length > 0
    ? r.modelImageUrls
    : (r.modelImageUrl ? [r.modelImageUrl] : (Array.isArray(r.referencePreviews) ? r.referencePreviews : []));
  const previews = Array.isArray(r.referencePreviews) ? r.referencePreviews.filter(Boolean) : [];
  const operatorName = readOperatorName(task, r);

  const modelImageUrls = await resolveUrlList(rawRefs);
  if (modelImageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingModel'));
  }

  const estimatedTotal = Math.max(1, modelImageUrls.length * generateCount);
  return enqueueModelStudioRetry(taskId, estimatedTotal, {
    ...r,
    prompt,
    userPrompt: prompt,
    generateCount,
    hasCustomModel: true,
    referencePreviews: previews.length > 0 ? previews : modelImageUrls,
    previewUrl: previews[0] || modelImageUrls[0] || '',
    modelImageUrls,
    modelImageUrl: modelImageUrls[0],
    modelCount: modelImageUrls.length,
    operator: operatorName,
    operatorName,
  });
}

/** 模特试衣历史重试：重新入队 */
export async function retryModelTryOnTask(task) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const prompt = String(r.prompt || r.userPrompt || '').trim();
  const generateCount = readGenerateCount(r, task);
  const garmentType = String(r.garmentType || '').trim() || undefined;
  const frameScope = String(r.frameScope || '').trim() || undefined;
  const modelType = String(r.modelType || '').trim() || undefined;
  const operatorName = readOperatorName(task, r);

  const garmentRaw = Array.isArray(r.garmentImageUrls) && r.garmentImageUrls.length > 0
    ? r.garmentImageUrls
    : (Array.isArray(r.referencePreviews)
      ? r.referencePreviews.slice(0, Math.max(1, Number(r.selectedGarmentCount) || r.referencePreviews.length - (r.hasCustomModel ? 1 : 0)))
      : []);
  const garmentImageUrls = await resolveUrlList(garmentRaw);
  if (garmentImageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingGarment'));
  }

  const { modelImageUrl, meta: presetMeta } = await resolveRetryModelImageUrl({
    navId: 'modelTryOn',
    result: r,
    batchImageCount: garmentImageUrls.length,
    uploadedErrorMessage: i18n.t('workbench:ipeUtilError.taskMissingModel'),
    presetErrorMessage: i18n.t('workbench:ipeUtilError.presetModelUnresolved'),
  });

  const estimatedTotal = Math.max(1, garmentImageUrls.length * generateCount);
  return enqueueModelStudioRetry(taskId, estimatedTotal, {
    ...r,
    prompt,
    userPrompt: prompt,
    generateCount,
    garmentType,
    frameScope,
    modelType,
    ...presetMeta,
    garmentImageUrls,
    modelImageUrl,
    referencePreviews: buildRetryReferencePreviews(r, garmentImageUrls, modelImageUrl),
    previewUrl: garmentImageUrls[0] || '',
    operator: operatorName,
    operatorName,
  });
}

/** 手持商品历史重试：重新入队 */
export async function retryModelHandheldTask(task) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const prompt = String(r.prompt || r.userPrompt || '').trim();
  const generateCount = readGenerateCount(r, task);
  const modelType = String(r.modelType || '').trim() || undefined;
  const modelEthnicity = String(r.modelEthnicity || '').trim() || undefined;
  const operatorName = readOperatorName(task, r);

  const productRaw = Array.isArray(r.productImageUrls) && r.productImageUrls.length > 0
    ? r.productImageUrls
    : (Array.isArray(r.referencePreviews)
      ? r.referencePreviews.slice(0, Math.max(1, Number(r.productCount) || r.referencePreviews.length - (r.hasCustomModel ? 1 : 0)))
      : []);
  const productImageUrls = await resolveUrlList(productRaw);
  if (productImageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingProduct'));
  }

  const { modelImageUrl, meta: presetMeta } = await resolveRetryModelImageUrl({
    navId: 'modelHandheld',
    result: r,
    batchImageCount: productImageUrls.length,
  });

  const estimatedTotal = Math.max(1, productImageUrls.length * generateCount);
  return enqueueModelStudioRetry(taskId, estimatedTotal, {
    ...r,
    prompt,
    userPrompt: prompt,
    generateCount,
    modelType,
    modelEthnicity,
    ...presetMeta,
    productImageUrls,
    productCount: productImageUrls.length,
    modelImageUrl,
    referencePreviews: buildRetryReferencePreviews(r, productImageUrls, modelImageUrl),
    previewUrl: productImageUrls[0] || '',
    operator: operatorName,
    operatorName,
  });
}

/** 万物穿戴历史重试：重新入队 */
export async function retryModelUniversalWearTask(task) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const prompt = String(r.prompt || r.userPrompt || '').trim();
  const generateCount = readGenerateCount(r, task);
  const modelType = String(r.modelType || '').trim() || undefined;
  const modelEthnicity = String(r.modelEthnicity || '').trim() || undefined;
  const operatorName = readOperatorName(task, r);

  const garmentRaw = Array.isArray(r.garmentImageUrls) && r.garmentImageUrls.length > 0
    ? r.garmentImageUrls
    : (Array.isArray(r.referencePreviews)
      ? r.referencePreviews.slice(0, Math.max(1, Number(r.garmentCount) || r.referencePreviews.length - (r.hasCustomModel ? 1 : 0)))
      : []);
  const garmentImageUrls = await resolveUrlList(garmentRaw);
  if (garmentImageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingWearGarment'));
  }

  const { modelImageUrl, meta: presetMeta } = await resolveRetryModelImageUrl({
    navId: 'modelUniversalWear',
    result: r,
    batchImageCount: garmentImageUrls.length,
  });

  const estimatedTotal = Math.max(1, garmentImageUrls.length * generateCount);
  return enqueueModelStudioRetry(taskId, estimatedTotal, {
    ...r,
    prompt,
    userPrompt: prompt,
    generateCount,
    modelType,
    modelEthnicity,
    ...presetMeta,
    garmentImageUrls,
    garmentCount: garmentImageUrls.length,
    modelImageUrl,
    referencePreviews: buildRetryReferencePreviews(r, garmentImageUrls, modelImageUrl),
    previewUrl: garmentImageUrls[0] || '',
    operator: operatorName,
    operatorName,
  });
}
